// Event type definitions for type safety
export type AnalyticsEvent =
  // Phase 1: Critical Events
  | { name: 'user_logged_in'; properties: { role: string; method: string } }
  | { name: 'user_logged_out'; properties: { session_duration_minutes: number } }
  | { name: 'error_encountered'; properties: { error_type: string; error_message: string; component?: string } }
  // Phase 2: High Value Events
  | { name: 'feature_accessed'; properties: { feature_name: string; page_path: string } }
  | { name: 'vintrace_credentials_added'; properties: { environment: string } }
  | { name: 'vintrace_sync_started'; properties: { sync_type: string } }
  | { name: 'vintrace_sync_completed'; properties: { sync_type: string; success: boolean; duration_seconds: number; records_synced?: number } }
  | { name: 'vintrace_conflict_resolved'; properties: { conflict_type: string; resolution: string } }
  // Phase 3: Optimization Events
  | { name: 'page_view'; properties: { page_path: string; page_title: string } }
  | { name: 'search_performed'; properties: { search_type: string; query: string; results_count: number } }
  | { name: 'client_created'; properties: { has_contact_info: boolean } }
  | { name: 'client_viewed'; properties: { client_id: string } }
  | { name: 'client_stats_accessed'; properties: { client_id: string; stats_viewed: string[] } }
  | { name: 'vessel_created'; properties: { type: string; capacity: number; material: string } }
  | { name: 'vessel_capacity_warning'; properties: { vessel_id: string; current_percent: number } }
  | { name: 'lot_created'; properties: { varietal: string; vintage: number; client_id: string } }
  | { name: 'user_invited'; properties: { role: string; inviter_id: string } }
  | { name: 'invitation_accepted'; properties: { invitation_id: string; time_to_acceptance_hours: number } }
  | { name: 'user_role_changed'; properties: { user_id: string; from_role: string; to_role: string } }
  | { name: 'dashboard_viewed'; properties: { view_type: string } }
  | { name: 'profile_updated'; properties: { fields_changed: string[] } }
  | { name: 'organization_settings_changed'; properties: { settings_changed: string[] } }
  | { name: 'notification_preferences_updated'; properties: { preferences_changed: string[] } }
  | { name: 'audit_log_accessed'; properties: { filters_applied: string[] } };

export class Analytics {
  track<T extends AnalyticsEvent>(_event: T): void {}
  identify(_userId: string, _properties?: Record<string, any>): void {}
  reset(): void {}
  setUserProperties(_properties: Record<string, any>): void {}
  pageView(pagePath: string, pageTitle: string): void {
    this.track({ name: 'page_view', properties: { page_path: pagePath, page_title: pageTitle } });
  }
}

const analyticsInstance = new Analytics();

export const getAnalytics = (): Analytics => analyticsInstance;

export const trackEvent = <T extends AnalyticsEvent>(_event: T): void => {};

export const calculateDurationMinutes = (startDate: Date | string, endDate: Date | string = new Date()): number => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
};

export const calculateDurationHours = (startDate: Date | string, endDate: Date | string = new Date()): number => {
  const minutes = calculateDurationMinutes(startDate, endDate);
  return Math.round(minutes / 60);
};

export const calculateDurationSeconds = (startDate: Date | string, endDate: Date | string = new Date()): number => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  return Math.round((end.getTime() - start.getTime()) / 1000);
};
